import { Link } from 'react-router-dom';
import type { MatrixAssignment, MatrixMonth, MatrixPerson } from '../../services/api/capacity';
import { formatFte } from '../../utils/format';
import { useFlag } from '../../services/flagsContext';
import { useConfirmAssignment } from '../../hooks/useCapacity';
import { formatAllocation, formatAllocationHours, formatAllocationPercent } from './allocationFormat';

interface PlanningMatrixProps {
  months: string[];
  people: MatrixPerson[];
}

function monthLabel(month: string): string {
  const [year, m] = month.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(m!, 10) - 1]} ${year!.slice(2)}`;
}

function personName(p: MatrixPerson): string {
  return [p.lastName, p.firstName].filter(Boolean).join(' ') || `User #${p.id}`;
}

// Cell class reads the backend's isOverallocated classification (computed in
// availabilityService.ts: behind `mks_535_percent_of_available` it is simply
// `allocatedPercent > 100`, off it the older `allocatedFte > availableFte *
// 1.05`) so the cell color and the OVER label are driven by a single source of
// truth — no local recomputation against rounded values (which used to produce
// green+OVER mismatches; CP-FIX-02).
function fteCellClass(allocated: number, isOverallocated: boolean): string {
  if (isOverallocated) return 'bg-red-50 text-red-700';
  if (allocated > 0) return 'bg-green-50 text-green-700';
  return '';
}

function utilizationColor(pct: number): string {
  if (pct >= 100) return 'text-red-600 font-bold';
  if (pct >= 80) return 'text-green-600 font-semibold';
  if (pct >= 50) return 'text-amber-600 font-semibold';
  if (pct > 0) return 'text-gray-500';
  return 'text-gray-300';
}

function computeUtilization(person: MatrixPerson, months: string[]): number {
  let totalAllocated = 0;
  let totalAvailable = 0;
  for (const m of months) {
    const data = person.months[m];
    if (data) {
      totalAllocated += data.allocatedFte;
      totalAvailable += data.availableFte;
    }
  }
  if (totalAvailable <= 0) return 0;
  return Math.round((totalAllocated / totalAvailable) * 100);
}

/**
 * MKS-358 — the FTE figures, now demoted to a tooltip. Everything a planner
 * used to read off the cell face is still one hover away: what the person is
 * booked at, the FTE they are on this month, and the hours that leaves.
 */
function cellTooltip(data: MatrixMonth, percentOfAvailable: boolean): string {
  return [
    // MKS-535 — name what the per cent on the face of the cell is a per cent
    // OF. Behind `mks_535_percent_of_available` that is the hours the person
    // can actually be booked for this month, and a reader who sees `125 %`
    // needs to know which 100 % it is over. With the flag off the per cent is
    // still of the month capacity before time off, and the tooltip keeps
    // today's wording rather than claiming a basis it does not have.
    ...(percentOfAvailable
      ? [
          `${formatAllocationHours(data.allocatedHours)} booked of ${formatAllocationHours(
            data.availableHours,
          )} available — ${formatAllocationPercent(data.allocatedPercent)}`,
        ]
      : []),
    `${formatFte(data.allocatedFte)} FTE allocated of ${formatFte(data.availableFte)} FTE available`,
    `This month: ${formatFte(data.fte)} FTE, ${formatAllocationHours(data.availableHours)} available`,
    `${data.workingDays} working days${data.timeOffDays > 0 ? `, ${data.timeOffDays} day(s) time off` : ''}`,
  ].join('\n');
}

function assignmentTooltip(a: MatrixAssignment): string {
  return `${a.projectName} (${a.roleCode ?? a.roleName}) — ${formatAllocation(
    a.share * 100,
    a.hours,
  )}, ${formatFte(a.fte)} FTE${a.status === 'tentative' ? ' (tentative)' : ''}`;
}

/**
 * MKS-535 — the two layouts of a person-month, picked by
 * `mks_535_planner_cell_fit`.
 *
 * `legacy` is what the grid does today: every assignment line is one row of
 * `max-w-[110px]` whose `truncate` sits on a `<span>`, where it does nothing
 * (ellipsis needs a block box with its own overflow), so a cell holding two or
 * three assignments paints its per cents, project links and Confirm buttons
 * straight over the neighbouring month. The month column is 120 px wide and the
 * table is `table-layout: fixed`, so there is nowhere for that text to go.
 *
 * `fit` bounds it on all three axes instead of hoping it fits:
 *  * the cell clips, so nothing can ever reach the next column;
 *  * the assignment list scrolls past a fixed height, so a person on eight
 *    projects does not stretch the whole row;
 *  * each line puts the per cent and the Confirm button on their own row and
 *    gives the project name the FULL column width underneath, wrapping over up
 *    to two lines — about 30 characters, against the ~8 that survive sharing a
 *    110 px line with a button. The full name stays in the line's `title`.
 *
 * `w-max min-w-full` on the table is the narrow-viewport half: with
 * `table-layout: fixed` and an auto width, a container narrower than the sum of
 * the columns squeezes them BELOW the widths set here, which is where the
 * overlap starts on a phone. Sized to its content, the table keeps its columns
 * and the wrapper (`overflow-auto`) scrolls, while `min-w-full` keeps it
 * filling a wide screen.
 */
const CELL_LAYOUT = {
  fit: {
    table: 'w-max min-w-full divide-y divide-gray-200 text-xs',
    monthCell: 'px-2 py-2 text-center align-top overflow-hidden',
    assignmentList: 'mt-0.5 space-y-0.5 max-h-24 overflow-y-auto',
    assignment: 'w-full',
  },
  legacy: {
    table: 'divide-y divide-gray-200 text-xs',
    monthCell: 'px-2 py-2 text-center',
    assignmentList: 'mt-0.5 space-y-0.5',
    assignment: 'max-w-[110px]',
  },
} as const;

/**
 * One assignment inside a person-month: its share of the person, the project it
 * is on, and (when it is only penciled in) the one-click confirm.
 */
function AssignmentLine({
  a,
  fit,
  onConfirm,
  isConfirming,
}: {
  a: MatrixAssignment;
  fit: boolean;
  onConfirm: (a: MatrixAssignment) => void;
  isConfirming: boolean;
}) {
  const layout = fit ? CELL_LAYOUT.fit : CELL_LAYOUT.legacy;
  const confirm =
    a.status === 'tentative' ? (
      <button
        type="button"
        onClick={() => onConfirm(a)}
        disabled={isConfirming}
        className={`font-medium text-primary-600 hover:underline disabled:opacity-50 ${
          fit ? 'shrink-0' : 'ml-1'
        }`}
        aria-label={`Confirm tentative assignment on ${a.projectName}`}
      >
        Confirm
      </button>
    ) : null;

  return (
    <div
      className={`text-[10px] leading-tight ${layout.assignment} ${
        a.status === 'tentative'
          ? 'rounded border border-dashed border-current px-0.5 opacity-80'
          : ''
      }`}
      title={assignmentTooltip(a)}
    >
      {fit ? (
        <>
          <div className="flex items-start justify-between gap-1">
            <span>{formatAllocationPercent(a.share * 100)}</span>
            {confirm}
          </div>
          <Link
            to={`/projects/${a.projectId}`}
            className="block line-clamp-2 break-words text-primary-600 hover:underline"
          >
            {a.projectName}
          </Link>
        </>
      ) : (
        <>
          <span className="truncate">
            {formatAllocationPercent(a.share * 100)}{' '}
            <Link to={`/projects/${a.projectId}`} className="text-primary-600 hover:underline">
              {a.projectName}
            </Link>
          </span>
          {confirm}
        </>
      )}
    </div>
  );
}

/**
 * One person-month behind `mks_358_planner_allocation_ui`.
 *
 * The face of the cell is the combined per cent and hours. When any of it is
 * only penciled in, the confirmed and tentative halves are listed under it so
 * both are separately readable; a cell with nothing tentative stays as short
 * as it is today. Tentative is outlined and dashed wherever it appears — the
 * over-allocation red keeps the whole-cell background, so it is still the
 * loudest thing on the row.
 */
function AllocationCell({
  data,
  onConfirm,
  isConfirming,
  percentOfAvailable,
  fit,
}: {
  data: MatrixMonth;
  onConfirm: (a: MatrixAssignment) => void;
  isConfirming: boolean;
  percentOfAvailable: boolean;
  fit: boolean;
}) {
  const hasTentative = data.tentativeHours > 0 || data.tentativePercent > 0;

  return (
    <>
      <div className="font-semibold" title={cellTooltip(data, percentOfAvailable)}>
        {data.allocatedHours > 0 || data.allocatedPercent > 0
          ? formatAllocation(data.allocatedPercent, data.allocatedHours)
          : '-'}
      </div>
      {hasTentative && (
        <div className="mt-0.5 space-y-0.5 text-[10px] leading-tight">
          <div title="Confirmed allocation">
            confirmed {formatAllocation(data.confirmedPercent, data.confirmedHours)}
          </div>
          <div
            className="rounded border border-dashed border-current px-0.5 opacity-80"
            title="Tentative allocation — planned, not confirmed"
          >
            tentative {formatAllocation(data.tentativePercent, data.tentativeHours)}
          </div>
        </div>
      )}
      {data.assignments.length > 0 && (
        <div className={(fit ? CELL_LAYOUT.fit : CELL_LAYOUT.legacy).assignmentList}>
          {data.assignments.map((a) => (
            <AssignmentLine
              key={a.id}
              a={a}
              fit={fit}
              onConfirm={onConfirm}
              isConfirming={isConfirming}
            />
          ))}
        </div>
      )}
      {data.isOverallocated && (
        <div
          className="text-[10px] text-red-600 font-bold"
          // MKS-535: with the flag on the badge IS the per cent above it, so
          // say which number it is reading rather than leaving the two to be
          // reconciled by eye.
          title={
            percentOfAvailable
              ? `Booked past the hours available this month — ${formatAllocationPercent(
                  data.allocatedPercent,
                )} of ${formatAllocationHours(data.availableHours)}`
              : undefined
          }
        >
          OVER
        </div>
      )}
    </>
  );
}

/**
 * The pre-MKS-358 cell: FTE on the face, no status anywhere.
 *
 * Its own lines already truncate (here the class sits on the block, where it
 * works), so MKS-535 only has to stop a person on a dozen projects from
 * stretching the row — hence the bounded list, and nothing else.
 */
function FteCell({ data, fit }: { data: MatrixMonth; fit: boolean }) {
  return (
    <>
      <div className="font-semibold">{data.allocatedFte > 0 ? formatFte(data.allocatedFte) : '-'}</div>
      {data.assignments.length > 0 && (
        <div className={(fit ? CELL_LAYOUT.fit : CELL_LAYOUT.legacy).assignmentList}>
          {data.assignments.map((a) => (
            <div key={a.id} className="text-[10px] leading-tight truncate max-w-[110px]" title={`${a.projectName} (${a.roleCode ?? a.roleName}) ${formatFte(a.fte)}`}>
              {formatFte(a.fte)} {a.roleCode ?? ''}{' '}
              <Link to={`/projects/${a.projectId}`} className="text-primary-600 hover:underline">{a.projectName}</Link>
            </div>
          ))}
        </div>
      )}
      {data.isOverallocated && <div className="text-[10px] text-red-600 font-bold">OVER</div>}
    </>
  );
}

export default function PlanningMatrix({ months, people }: PlanningMatrixProps) {
  const allocationUi = useFlag('mks_358_planner_allocation_ui');
  // MKS-535: read once for the whole grid rather than per cell — a twelve-month
  // matrix of a hundred people is twelve hundred context reads otherwise.
  const percentOfAvailable = useFlag('mks_535_percent_of_available');
  // MKS-535 — the cell that holds several assignments without painting over
  // its neighbour. Read once for the same reason as the line above.
  const fit = useFlag('mks_535_planner_cell_fit');
  const layout = fit ? CELL_LAYOUT.fit : CELL_LAYOUT.legacy;
  // One mutation for the whole grid rather than one per cell: a twelve-month
  // matrix of a hundred people is twelve hundred cells, and each would
  // otherwise register its own mutation observer.
  const confirmAssignment = useConfirmAssignment();

  if (people.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-400">No people with role tags or capacity assignments.</p>;
  }

  return (
    <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
      <table className={layout.table} style={{ tableLayout: 'fixed' }}>
        <thead className="bg-gray-50">
          <tr>
            <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left font-medium text-gray-500 uppercase w-[180px]">Person</th>
            <th className="sticky left-[180px] z-20 bg-gray-50 px-2 py-2 text-left font-medium text-gray-500 uppercase w-[80px]">Roles</th>
            <th className="sticky left-[260px] z-20 bg-gray-50 px-2 py-2 text-center font-medium text-gray-500 uppercase w-[50px]">Util.</th>
            {months.map((m) => (
              <th key={m} className="px-2 py-2 text-center font-medium text-gray-500 uppercase whitespace-nowrap w-[120px]">
                {monthLabel(m)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {people.map((person) => {
            const utilPct = computeUtilization(person, months);
            return (
              <tr key={person.id} className="hover:bg-gray-50">
                <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                  {personName(person)}
                </td>
                <td className="sticky left-[180px] z-10 bg-white px-2 py-2 text-gray-500 whitespace-nowrap">
                  {person.roleTags.map((r) => r.code || r.name.slice(0, 3)).join(', ')}
                </td>
                <td className={`sticky left-[260px] z-10 bg-white px-2 py-2 text-center ${utilizationColor(utilPct)}`}>
                  {utilPct}%
                </td>
                {months.map((m) => {
                  const data = person.months[m];
                  if (!data) return <td key={m} className={`${layout.monthCell} text-gray-300`}>-</td>;
                  return (
                    <td key={m} className={`${layout.monthCell} ${fteCellClass(data.allocatedFte, data.isOverallocated)}`}>
                      {allocationUi ? (
                        <AllocationCell
                          data={data}
                          onConfirm={(a) =>
                            confirmAssignment.mutate({
                              projectId: a.projectId,
                              assignmentId: a.assignmentId,
                            })
                          }
                          isConfirming={confirmAssignment.isPending}
                          percentOfAvailable={percentOfAvailable}
                          fit={fit}
                        />
                      ) : (
                        <FteCell data={data} fit={fit} />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
